import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  MapPin,
  Building,
  User,
  Calendar,
  Ruler,
  TrendingUp,
  FileText,
  Eye,
} from "lucide-react";
import { Button } from "../../components/AdminSharedComponents";
import StatusBadge from "./StatusBadge";

const ExcavationDetail = ({ excavation, onBack }) => {
  const formatDate = (dateString) => {
    if (!dateString) return "Not set";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div className="h-full flex flex-col">
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center gap-3 mb-2">
          <Button variant="secondary" size="sm" onClick={onBack}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h2 className="text-lg font-semibold text-primary-dark">
            Excavation Details
          </h2>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        <div className="space-y-4">
          <div>
            <h3 className="text-xl font-bold text-primary-dark mb-2">
              {excavation.name || "Unnamed Excavation Point"}
            </h3>
            <StatusBadge status={excavation.status} />
          </div>

          <div className="space-y-3">
            {excavation.Project && (
              <div className="flex items-start gap-3">
                <Building className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="text-sm font-medium text-gray-900">
                    Project
                  </div>
                  <div className="text-sm text-gray-600">
                    {excavation.Project.name}
                  </div>
                </div>
              </div>
            )}

            {excavation.depth && (
              <div className="flex items-start gap-3">
                <Ruler className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="text-sm font-medium text-gray-900">Depth</div>
                  <div className="text-sm text-gray-600">
                    {excavation.depth} meters
                  </div>
                </div>
              </div>
            )}

            {excavation.contractor && (
              <div className="flex items-start gap-3">
                <User className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="text-sm font-medium text-gray-900">
                    Contractor
                  </div>
                  <div className="text-sm text-gray-600">
                    {excavation.contractor}
                  </div>
                </div>
              </div>
            )}

            {excavation.start_date && (
              <div className="flex items-start gap-3">
                <Calendar className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="text-sm font-medium text-gray-900">
                    Start Date
                  </div>
                  <div className="text-sm text-gray-600">
                    {formatDate(excavation.start_date)}
                  </div>
                </div>
              </div>
            )}

            {excavation.end_date && (
              <div className="flex items-start gap-3">
                <Calendar className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="text-sm font-medium text-gray-900">
                    End Date
                  </div>
                  <div className="text-sm text-gray-600">
                    {formatDate(excavation.end_date)}
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-start gap-3">
              <Calendar className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
              <div>
                <div className="text-sm font-medium text-gray-900">Created</div>
                <div className="text-sm text-gray-600">
                  {formatDate(excavation.created_at)}
                </div>
              </div>
            </div>

            {excavation.progress !== undefined &&
              excavation.progress !== null && (
                <div className="flex items-start gap-3">
                  <TrendingUp className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900">
                      Progress
                    </div>
                    <div className="text-sm text-gray-600 mb-2">
                      {excavation.progress}% completed
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${excavation.progress}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              )}

            {excavation.description && (
              <div className="flex items-start gap-3">
                <FileText className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="text-sm font-medium text-gray-900">
                    Description
                  </div>
                  <div className="text-sm text-gray-600 leading-relaxed">
                    {excavation.description}
                  </div>
                </div>
              </div>
            )}

            {excavation.coordinates && (
              <div className="flex items-start gap-3">
                <MapPin className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="text-sm font-medium text-gray-900">
                    Coordinates
                  </div>
                  <div className="text-sm text-gray-600">
                    <div>Latitude: {excavation.coordinates.lat}</div>
                    <div>Longitude: {excavation.coordinates.lng}</div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="pt-4 border-t border-gray-200">
            <Link
              to={`/admin/projects/excavation/show/${excavation.id}`}
              className="block w-full"
            >
              <Button icon={Eye} className="w-full">
                Show Point Detail
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExcavationDetail;
